import { CommonModule, CurrencyPipe, JsonPipe } from '@angular/common';
import { Component, computed, Signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngxs/store';
import { getYear, subYears } from 'date-fns';
import { DebtScheduleItemsTableComponent } from 'ng2-app/components/debt-schedule/debt-schedule-items.table.component';
import { DebtScheduleItemDialogComponent } from 'ng2-app/components/dscr/debt-schedule-item.dialog.component';
import {
  DscrDocRequirement,
  DscrDocRequirementComponent
} from 'ng2-app/components/dscr/doc-requirements/dscr-doc-requirement.component';
import { DscrResultsTableComponent } from 'ng2-app/components/dscr/profit-loss-statements/dscr-results.table.component';
import {
  ProfitLossStatementDialogComponent
} from 'ng2-app/components/dscr/profit-loss-statements/profit-loss-statement.dialog.component';
import { TooltipIfClampedDirective } from 'ng2-app/directives/tooltip-if-clamped.directive';
import { BorrowerValue } from 'ng2-app/models/dto/borrower-value.model';
import { Contact } from 'ng2-app/models/dto/contact.model';
import { ConfirmationDialogComponent } from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.component';
import { SaveBorrowerValues } from 'ng2-app/store/borrower-value/borrower-value.actions';
import { BorrowerValueState } from 'ng2-app/store/borrower-value/borrower-value.state';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { ContactState } from 'ng2-app/store/contact/contact.state';
import { GetDebtSchedules } from 'ng2-app/store/debt-schedule/debt-schedule.actions';
import { GetBorrowerDocuments } from 'ng2-app/store/documents/document.actions';
import { DocumentState } from 'ng2-app/store/documents/document.state';
import { FlashSpreadPusherSubscribe, GetFlashSpreadData } from 'ng2-app/store/flashspread/flashspread.actions';
import { FlashSpreadState } from 'ng2-app/store/flashspread/flashspread.state';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import {
  GetDscrResults,
  GetProfitLossStatements,
  ProcessDscrResults
} from 'ng2-app/store/profit-loss-statements/dscr.actions';
import { DscrState } from 'ng2-app/store/profit-loss-statements/dscr.state';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-dscr',
  templateUrl: 'dscr.component.html',
  standalone: true,
  imports: [
    CommonModule,
    JsonPipe,
    MatCardModule,
    MatChipsModule,
    MatTableModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatListModule,
    MatExpansionModule,
    DebtScheduleItemsTableComponent,
    MatButtonModule,
    DscrResultsTableComponent,
    MatSelectModule,
    MatMenuModule,
    MatTooltipModule,
    TooltipIfClampedDirective,
    DscrDocRequirementComponent,
    MatProgressSpinnerModule,
  ],
})
export class DscrComponent {

  /**
   * Form Controls
   */
  personalDebtPaymentControl = new FormControl<number>(0);
  mortgagePaymentControl = new FormControl<number>(0);
  taxDocSearchControl = new FormControl<string>('');
  numAdultsControl = new FormControl<number>(0);
  numChildrenControl = new FormControl<number>(0);

  /**
   * Computed / State Selectors
   */
  borrowerId$ = this.store.select( BorrowerState.borrowerId );
  borrowerId = toSignal( this.store.select( BorrowerState.borrowerId ) );
  borrowerValuesLoading = toSignal( this.store.select( BorrowerValueState.loading ) );
  dscrStateLoading = toSignal( this.store.select( DscrState.loading ) );
  mortagePaymentAmount$ = this.store.select( BorrowerValueState.valueSelectorByAlias('rent_or_mortgage_amount') );
  mortgagePaymentAmountValue = toSignal(this.mortagePaymentAmount$);
  personalDebtPaymentAmount$ = this.store.select( BorrowerValueState.valueSelectorByAlias('monthlyPaymentOnInstallmentTradelines') );
  personalDebtPaymentAmountValue = toSignal(this.personalDebtPaymentAmount$);
  numAdultsBorrowerValue$ = this.store.select( BorrowerValueState.valueSelectorByAlias('taxesNumberOfAdults') );
  numAdultsValue = toSignal( this.numAdultsBorrowerValue$ );
  numChildrenBorrowerValue$ = this.store.select( BorrowerValueState.valueSelectorByAlias('taxesNumberOfChildren') );
  numChildrenValue = toSignal( this.numChildrenBorrowerValue$ );
  maxNewLoanAmountsByLender = toSignal( this.store.select( DscrState.maxNewLoanAmountsByLender ) );
  canSearchTaxForms = toSignal( this.store.select( LenderUserState.hasRole('developer') ) );
  businessDocuments = toSignal( this.store.select( FlashSpreadState.businessTaxDocumentsData ) );
  personalDocuments = toSignal( this.store.select( FlashSpreadState.personalTaxDocumentsData ) );
  currentYearProfitLoss = toSignal( this.store.select( DscrState.currentYearProfitLoss ) );
  showMaxNewLoanAmounts = computed(() => {
    const amountsByLender = this.maxNewLoanAmountsByLender();
    return amountsByLender?.length && !this.dscrStateLoading();
  });

  contacts: Signal<Contact[] | undefined> = toSignal( this.store.select( ContactState.contacts) as Observable<Contact[]> );
  flashSpreadDocumentData = toSignal( this.store.select( FlashSpreadState.documentsData ) );
  borrowerDocumentsBase = toSignal( this.store.select( DocumentState.documents ) );
  borrowerDocuments = computed(() => {
    const documents = this.borrowerDocumentsBase() || [],
      flashSpreadDocuments = this.flashSpreadDocumentData() || [];
    return documents.map( document => {
      const flash_spread_data = document.flash_spread_data || flashSpreadDocuments.find( fsDoc => fsDoc.documentId === document.id );
      return {
        ...document,
        flash_spread_data,
      };
    });
  });
  businessRequirements = computed(() => {
    const borrowerDocuments = this.borrowerDocuments() || [];
    return this.yearsOptions.map( year => {
      const matchingBorrowerDocument = borrowerDocuments.find(doc => {
        const byCategory = doc.category === 'businessTaxReturns';
        const byDocYear = doc.years.includes(year.toString());
        const byFsDataYear = doc.flash_spread_data?.forms?.find( form => form.year === year);
        return byCategory && (byDocYear || byFsDataYear);
      });

      return {
        year,
        documentData: matchingBorrowerDocument,
      };
    });
  });
  unassignedBorrowerDocuments = computed(() => {
    const borrowerDocuments = this.borrowerDocuments() || [];
    return borrowerDocuments.filter( borrowerDoc => {
      const currentlySatisfiesPersonalRequirement = borrowerDoc.category === 'personalTaxReturns' && !!borrowerDoc.contactId;
      return !currentlySatisfiesPersonalRequirement && borrowerDoc.category !== 'businessTaxReturns';
    });
  });
  businessDocumentsData = computed(() => {
    const search = this.search();
    const documents = this.businessDocuments() || [];
    return documents
      .flatMap(doc => doc.forms)
      .map(form => {
        return {
          //@ts-ignore
          title: `${form.type} - (${form.year})`,
          data: Object.keys(form).reduce((agg, cur) => {
            const fields = cur === 'fields' || cur === 'entity' ? form[cur] : form[cur]?.fields;
            if(!!fields) {
              return [
                ...agg,
                {
                  title: cur,
                  fields: Object.keys(fields).map( key => ({
                    key,
                    value: fields[key],
                  })).filter(field => {
                    return !search || field.key.includes(search) || field.value?.toString()?.includes(search);
                  }),
                },
              ];
            }
            return agg;
          }, []).filter(data => !!data.fields.length),
        };
    });
  });

  personalDocumentsData = computed(() => {
    const search = this.search();
    const documents = this.personalDocuments() || [];
    return documents
      .flatMap(doc => doc.forms)
      .map(form => {
        return {
          //@ts-ignore
          title: `${form.type} - (${form.year})`,
          data: Object.keys(form).reduce((agg, cur) => {
            const fields = cur === 'fields' || cur === 'entity' ? form[cur] : form[cur]?.fields;
            if(!!fields) {
              return [
                ...agg,
                {
                  title: cur,
                  fields: Object.keys(fields).map( key => ({
                    key,
                    value: fields[key],
                  })).filter(field => {
                    return !search || field.key.includes(search) || field.value?.toString()?.includes(search);
                  }),
                },
              ];
            }
            return agg;
          }, []).filter(data => !!data.fields.length),
        };
      });
  });

  search = toSignal(this.taxDocSearchControl.valueChanges);

  ownerContacts = computed(() => {
    const borrowerDocuments = this.borrowerDocuments() || [];
    return this.contacts()?.filter( contact => {
      // return contact.isPrimary || ((contact.percentOwnership || 0) >= 20);
      // temporarily scope to only the primary contact until the feature can fully handle additional owners
      return contact.isPrimary;
    })
      .sort((a, b) => a.isPrimary ? -1 : b.isPrimary ? 1 : 0)
      .map(owner => {
        const ownerDocuments = borrowerDocuments.filter(doc => doc.contactId === owner.id);
        const ownerDocumentsWithNoYear = ownerDocuments.filter(doc => !doc.years?.length);
        const personalTaxDocReqs = this.personalTaxReturnReqs.map( req => {
          const data = ownerDocuments
            .find(doc => {
              const fromFsData = !!doc.flash_spread_data?.forms?.find(form => form.year === req.year);
              const matchingDocument = doc.years.includes(req.year.toString()) && doc.category === 'personalTaxReturns';
              return matchingDocument || fromFsData;
            });
          return {
            ...req,
            documentData: data,
          };
        });
        return {
          ...owner,
          personalTaxDocReqs,
          ownerDocumentsWithNoYear,
        };
      });
  });
  primaryContact = toSignal( this.store.select( ContactState.primaryContact ) );

  yearsOptions = Array.from({length: 2}, (__, i) => getYear(subYears(Date.now(), i + 1)));

  personalTaxReturnReqs: DscrDocRequirement[] = this.yearsOptions.map( year => ({
      year,
    }));

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private currencyPipe: CurrencyPipe,
  ) {
    this.borrowerId$.pipe(
      takeUntilDestroyed(),
    ).subscribe( borrowerId => {
      this.store.dispatch( new GetFlashSpreadData(borrowerId) );
      this.store.dispatch( new FlashSpreadPusherSubscribe(borrowerId) );
      this.store.dispatch( new GetDebtSchedules(borrowerId) );
      this.store.dispatch( new GetBorrowerDocuments(borrowerId) );
      this.store.dispatch( new GetProfitLossStatements(borrowerId) );
      this.store.dispatch( new GetDscrResults(borrowerId) );
    });

    this.mortagePaymentAmount$.pipe(
      takeUntilDestroyed(),
      filter(bv => !!bv),
    ).subscribe( bv => {
      this.mortgagePaymentControl.setValue(bv?.value, {emitEvent: false});
    });

    this.personalDebtPaymentAmount$.pipe(
      takeUntilDestroyed(),
      filter(bv => !!bv),
    ).subscribe( bv => {
      this.personalDebtPaymentControl.setValue(bv?.value, {emitEvent: false});
    });

    this.numChildrenBorrowerValue$.pipe(
      takeUntilDestroyed(),
      filter(bv => !!bv),
    ).subscribe( bv => {
      this.numChildrenControl.setValue(bv?.value, {emitEvent: false});
    });

    this.numAdultsBorrowerValue$.pipe(
      takeUntilDestroyed(),
      filter(bv => !!bv),
    ).subscribe( bv => {
      this.numAdultsControl.setValue(bv?.value, {emitEvent: false});
    });

    window.stuff = this;
  }

  handleManualInputChange($event, borrowerValue: BorrowerValue) {
    const borrowerId = this.borrowerId() as number;
    const value = $event.target?.value;
    if(value) {
      const confirmValue = borrowerValue.type === 'currency'
          ? this.currencyPipe.transform(value)
          : value;
      const previousValue = borrowerValue.value
        ? borrowerValue.type === 'currency'
          ? this.currencyPipe.transform(borrowerValue.value)
          : value
        : 'None';
      const dialogRef = this.dialog.open( ConfirmationDialogComponent, {
        data: {
          headerText: 'Confirm change to Borrower Value',
          confirmationPromptText: `Changing ${borrowerValue.shortName} from ${previousValue} to ${confirmValue}`,
          showCancelButton: true,
        },
      });
      dialogRef.componentInstance.confirm.pipe(
        take(1),
      ).subscribe(() => {
        this.store.dispatch( new SaveBorrowerValues(
          borrowerId,
          [{
            ...borrowerValue,
            value,
          }],
        ));
        dialogRef.close();
      });
    }
  }

  editCurrentYearProfitLoss() {
    const plData = this.currentYearProfitLoss()?.reduce((aggregate, current) => {
      return {
        ...aggregate,
        [current.alias]: current.value,
      };
    }, {});
    this.dialog.open( ProfitLossStatementDialogComponent, {
      width: '400px',
      data: {
        ...plData,
        editing: true,
      },
    });
  }

  newDebtScheduleItem() {
    this.dialog.open( DebtScheduleItemDialogComponent, {
      width: '400px',
    });
  }

  reprocessDscr() {
    this.store.dispatch( new ProcessDscrResults(this.borrowerId() as number) );
  }
}
